import pick from 'lodash/fp/pick'

import { EnrichedText, QuoteVariableType } from '@sequencehq/quote-content'

export type NestedContent =
  | {
      type: 'text'
      text: string
      styles: {
        bold?: boolean
        italic?: boolean
        strike?: boolean
        underline?: boolean
        textColor?: string
        backgroundColor?: string
      }
    }
  | { type: 'link'; content: NestedContent[]; href: string }
  | {
      type: 'variable'
      props: { variableType: string }
      content: NestedContent[]
    }

export const textContentAdapter = {
  in: (content: string | string[] | EnrichedText[]): NestedContent[] => {
    if (typeof content === 'string') {
      return [
        {
          type: 'text',
          text: content,
          styles: {}
        }
      ]
    }

    if (content.length === 0) {
      return [
        {
          type: 'text',
          text: '',
          styles: {}
        }
      ]
    }

    if (typeof content[0] === 'string') {
      return (content as (string | null)[])
        .map(item => (item === null ? '' : item))
        .map(item => ({
          type: 'text',
          text: item,
          styles: {}
        }))
    }

    return (content as EnrichedText[]).reduce(
      (acc: NestedContent[], item: EnrichedText) => {
        const previousItem = acc[acc.length - 1]

        /**
         * VARIABLES
         */
        if (
          previousItem &&
          previousItem.type === 'variable' &&
          previousItem?.props.variableType === item.variable
        ) {
          return [
            ...acc.slice(0, -1),
            {
              ...previousItem,
              content: [
                ...previousItem.content,
                ...(item.href
                  ? [
                      {
                        type: 'link' as const,
                        content: [
                          {
                            text: item.text,
                            type: 'text' as const,
                            styles: pick([
                              'bold',
                              'italic',
                              'underline',
                              'strike',
                              'textColor',
                              'backgroundColor'
                            ])(item.styles)
                          }
                        ],
                        href: item.href
                      }
                    ]
                  : [
                      {
                        text: item.text,
                        type: 'text' as const,
                        styles: pick([
                          'bold',
                          'italic',
                          'underline',
                          'strike',
                          'textColor',
                          'backgroundColor'
                        ])(item.styles)
                      }
                    ])
              ]
            }
          ]
        }

        if (item.variable) {
          return [
            ...acc,
            {
              type: 'variable' as const,
              content: [
                ...(item.href
                  ? [
                      {
                        type: 'link' as const,
                        content: [
                          {
                            text: item.text,
                            type: 'text' as const,
                            styles: pick([
                              'bold',
                              'italic',
                              'underline',
                              'strike',
                              'textColor',
                              'backgroundColor'
                            ])(item.styles)
                          }
                        ],
                        href: item.href
                      }
                    ]
                  : [
                      {
                        text: item.text,
                        type: 'text' as const,
                        styles: pick([
                          'bold',
                          'italic',
                          'underline',
                          'strike',
                          'textColor',
                          'backgroundColor'
                        ])(item.styles)
                      }
                    ])
              ],
              props: {
                variableType: item.variable
              }
            }
          ]
        }

        /**
         * LINKS
         */
        if (
          previousItem &&
          'href' in previousItem &&
          previousItem?.href === item.href
        ) {
          return [
            ...acc.slice(0, -1),
            {
              ...previousItem,
              content: [
                ...previousItem.content,
                {
                  text: item.text,
                  type: 'text' as const,
                  styles: pick([
                    'bold',
                    'italic',
                    'underline',
                    'strike',
                    'textColor',
                    'backgroundColor'
                  ])(item.styles)
                }
              ]
            }
          ]
        }

        if (item.href) {
          return [
            ...acc,
            {
              type: 'link' as const,
              content: [
                {
                  text: item.text,
                  type: 'text' as const,
                  styles: pick([
                    'bold',
                    'italic',
                    'underline',
                    'strike',
                    'textColor',
                    'backgroundColor'
                  ])(item.styles)
                }
              ],
              href: item.href
            }
          ]
        }

        return [
          ...acc,
          {
            type: 'text' as const,
            text: item.text,
            styles: pick([
              'bold',
              'italic',
              'underline',
              'strike',
              'textColor',
              'backgroundColor'
            ])(item.styles)
          }
        ]
      },
      []
    )
  },
  out: (content: NestedContent[]): EnrichedText[] => {
    return content.flatMap(item => {
      if ('text' in item) {
        return {
          styles: pick([
            'bold',
            'italic',
            'underline',
            'strike',
            'textColor',
            'backgroundColor'
          ])(item.styles),
          text: item.text
        } as EnrichedText
      }

      if (item.content.length) {
        if (item.type === 'variable') {
          return textContentAdapter.out(item.content).map(textContent => {
            return {
              ...textContent,
              variable: item.props.variableType as QuoteVariableType
            }
          })
        }

        return textContentAdapter.out(item.content).map(textContent => ({
          ...textContent,
          /**
           * It's highly unlikely the user intends to have a link
           * to another part of the customer portal, so let's assume
           * a lack of http or https means they still actually intended
           * to go to a different website.
           */
          href: item.href.includes('http') ? item.href : `https://${item.href}`
        }))
      }

      return []
    })
  }
}
